<template>
    <v-card-title>
        <b>{{ title }}</b>
    </v-card-title>
    <v-card-text>
        <v-data-table-virtual :items="rollen" :headers="headers" fixed-footer>
            <template #item.rollenName="{ item }">
                <div class="d-flex flex-wrap justify-content-start">
                    {{ item.rollenName.name }}
                </div>
            </template>

            <template #item.studiengangIds="{ item }">
                <div class="d-flex flex-wrap justify-content-start">
                    <div
                        v-for="(shortname, index) in studiengangShortNames(item.studiengangIds)"
                        :key="index"
                        class="flex-item mr-3"
                    >
                        {{ shortname }}
                    </div>
                </div>
            </template>

            <template #item.fachbereichIds="{ item }">
                <div class="d-flex flex-wrap justify-content-start">
                    <div
                        v-for="(shortname, index) in fachbereichShortNames(item.fachbereichIds)"
                        :key="index"
                        class="flex-item mr-3"
                    >
                        {{ shortname }}
                    </div>
                </div>
            </template>

            <template #item.isForAll="{ item }">
                <v-checkbox v-model="item.isForAll" name="check-button" disabled />
            </template>
            <template #item.actions="{ item }">
                <v-btn
                    icon="mdi-lead-pencil"
                    color="primary-lighten-2"
                    class="mr-1 flex-item"
                    @click="editRolle(item)"
                />
                <v-btn
                    icon="mdi-trash-can"
                    color="primary-lighten-2"
                    class="mr-1"
                    @click="confirmRemove(item)"
                />
            </template>
            <template #bottom>
                <div class="pa-4 text-center">
                    <v-btn
                        icon="mdi-plus"
                        color="primary"
                        title="Neue Rolle anlegen"
                        @click="newRolle()"
                    />
                </div>
            </template>
        </v-data-table-virtual>
    </v-card-text>
    <edit-rolle-modal
        v-if="!isLoading && userId"
        :user-id="userId"
        :is-new-rolle="isNewRolle"
        :rolle="rolleToEdit"
    />
    <v-dialog v-model="showRemoveRolleModal">
        <v-card>
            <v-card-title>Rolle {{ rolleToRemove?.rollenName?.name }} löschen?</v-card-title>
            <v-card-text> Sind Sie sicher?</v-card-text>
            <v-card-actions>
                <v-btn @click="removeRolle()"> Löschen</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup>
    import { computed, onMounted, ref } from 'vue'
    import {
        useAlertStore,
        useFachbereichStore,
        useModuleStore,
        useStudiengangStore,
        useUserStore
    } from '@/js/stores'
    import EditRolleModal from '@/js/components/Rolle/EditRolleModal.vue'

    const props = defineProps({
        userId: {
            type: String,
            required: false,
            default: () => ''
        }
    })

    const headers = ref([
        {
            key: 'rollenName',
            title: 'Rolle',
            sortable: false
        },
        {
            key: 'fachbereichIds',
            title: 'Fachbereiche',
            sortable: false
        },
        {
            key: 'studiengangIds',
            title: 'Studiengänge',
            sortable: false
        },
        {
            key: 'isForAll',
            title: 'für Alle'
        },
        {
            key: 'actions',
            title: 'Aktionen'
        }
    ])

    const rolleToEdit = ref({})
    const rolleToRemove = ref({})
    const isNewRolle = ref(false)
    const alertStore = useAlertStore()
    const userStore = useUserStore()
    const fachbereichStore = useFachbereichStore()
    const studiengangStore = useStudiengangStore()
    const modulStore = useModuleStore()
    const showRemoveRolleModal = ref(false)

    onMounted(() => {})

    const isLoading = computed(
        () =>
            userStore.isLoading ||
            fachbereichStore.isLoading ||
            studiengangStore.isLoading ||
            modulStore.isLoading
    )
    const user = computed(() => (props.userId ? userStore.getUserById(props.userId) : {}))
    const title = computed(() => `Rollen für ${user?.value.email?.email}`)
    const rollen = computed(() =>
        props.userId
            ? Object.keys(user.value.rollen).map((rolleKey) => user.value.rollen[rolleKey])
            : []
    )

    const editRolle = (rolle) => {
        isNewRolle.value = false
        rolleToEdit.value = Object.assign({}, rolle)
    }

    const confirmRemove = (rolle) => {
        rolleToRemove.value = rolle
        showRemoveRolleModal.value = true
    }

    const removeRolle = async function() {
        await userStore.removeRolle(props.userId, rolleToRemove.value.rollenName.name)
        showRemoveRolleModal.value = false
        if (!userStore.hasErrors) {
            alertStore.success('Rolle wurde entfernt.', 'Info')
        } else {
            alertStore.error('Fehler beim Entfernen der Rolle.', 'Fehler!')
        }
    }

    const newRolle = function() {
        isNewRolle.value = true
        rolleToEdit.value = {
            rollenName: { name: '' },
            fachbereichIds: [],
            studiengangIds: [],
            modulIds: [],
            isForAll: false
        }
    }

    const fachbereichShortNames = (fachbereichIds) => {
        return fachbereichIds
            .map((fachbereichId) => fachbereichStore.getFachbereichById(fachbereichId).kurzName)
            .sort((obj1, obj2) => (obj1 < obj2 ? -1 : 1))
    }

    const studiengangShortNames = (studiengangIds) => {
        return studiengangIds
            .map((studiengangId) => studiengangStore.getStudiengangById(studiengangId).kurzName)
            .sort((obj1, obj2) => (obj1 < obj2 ? -1 : 1))
    }
</script>

<style lang="scss" scoped></style>
